/**
 * Base pagination parameters
 */
export interface PaginationParams {
  page?: number;
  pageSize?: number;
}

/**
 * Sort direction type
 */
export type SortDirection = "asc" | "desc";

/**
 * Sort parameters interface
 */
export interface SortParams {
  sortBy?: string;
  sortDirection?: SortDirection;
}

/**
 * Base filter parameters interface
 * All specific filter types should extend this
 */
export interface FilterParams {
  [key: string]: string | number | boolean | Record<string, any> | undefined;
}

/**
 * Selection parameters interface
 */
export interface SelectionParams {
  fields?: string;
}

/**
 * Common query parameters combining pagination, sorting and filtering
 */
export interface QueryParams extends PaginationParams, SortParams, FilterParams, SelectionParams {}

/**
 * Paginated response interface
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
}

/**
 * Helper function to convert sort parameters to the backend format
 * @param sortBy Field to sort by
 * @param sortDirection Direction of sort ('asc' or 'desc')
 * @returns Formatted sort string for the backend
 */
export function formatSortParam(sortBy?: string, sortDirection?: SortDirection): string | undefined {
  if (!sortBy) return undefined;

  const prefix = sortDirection === "desc" ? "-" : "+";
  return `${prefix}${sortBy}`;
}

/**
 * Helper function to build query parameters for HTTP requests
 * @param params Query parameters
 * @returns Processed query parameters object with formatted sort_by
 */
export function buildQueryParams(params: QueryParams = {}): Record<string, string | number | boolean> {
  if (!params) {
    return {};
  }

  const { sortBy, sortDirection, ...restParams } = params;

  // Convert individual sort parameters to backend format (sort_by=+field or sort_by=-field)
  const queryParams: Record<string, string | number | boolean> = {};

  // Process filters and other parameters
  Object.entries(restParams).forEach(([key, value]) => {
    if (value !== undefined) {
      // Handle object values by converting them appropriately
      if (typeof value === "object" && value !== null) {
        if (key === "filters") {
          // If this is the filters object, flatten its contents into individual filter params
          const filters = value as Record<string, any>;
          Object.entries(filters).forEach(([filterKey, filterValue]) => {
            if (filterValue !== undefined && filterValue !== null && filterValue !== "") {
              // For objects nested inside filters, stringify them
              if (typeof filterValue === "object") {
                queryParams[filterKey] = JSON.stringify(filterValue);
              } else {
                queryParams[filterKey] = filterValue as string | number | boolean;
              }
            }
          });
        } else if (Array.isArray(value)) {
          // Handle arrays by joining them
          queryParams[key] = (value as any[]).join(",");
        } else {
          // For other objects, stringify them
          queryParams[key] = JSON.stringify(value);
        }
      } else {
        queryParams[key] = value as string | number | boolean;
      }
    }
  });

  if (sortBy) {
    queryParams.sort_by = formatSortParam(sortBy, sortDirection) ?? "";
  }

  // Convert any field[operator] filters to the proper format
  Object.entries(params).forEach(([key, value]) => {
    // Check if it's an operator-based filter (e.g., price[gte]) and not undefined
    if (key.includes("[") && key.includes("]") && value !== undefined) {
      queryParams[key] = value as string | number | boolean;
    }
  });

  return queryParams;
}
